import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { allPointOfInterestTemplates as allPointOfInterestTemplatesState } from '@/components/Analysis/state';
import { Can } from '@/components/Can';
import { POIDialogTab } from './Summary/style';
import { abyssColors } from '@/theme/colors';
import { PointOfInterestsQueryType, TemplateTypeWithAllTab } from '@/types';
import {
  PointOfInterestsDocument,
  PointOfInterestsQuery,
  usePointOfInterestsQuery,
} from '@/__generated__/graphql';
import {
  useUpdatePointOfInterestsStatusMutation,
  StatusRemediatedThrough,
  useUpdatePaintRegionStatusMutation,
  AllPaintRegionsForPaintBlockTableDocument,
  PaintRegionStatus,
  PoiStatus,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { upperSnakeCase } from '@/utils';
import { scrollDesign } from '../styles';
import { PageLoader } from '@/components/shared/PageLoader';
import { CustomTable } from '@/components/Analysis/shared/CustomTable/CustomTable';
import { ALL_POIS_TAB_LABEL } from '@/constants';
import usePoiData, { RowData } from '../PointOfInterests/usePoiData';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { usePoiColumns } from '@/hooks/usePoiColumns';

type PoiDialogProps = {
  isOpen: boolean;
  toggleDialog: (status: boolean) => void;
  paintRegionId: string;
};

type SelectedPois = {
  [key: string]: string[];
};
export const PoiDialogBox = ({ isOpen, toggleDialog, paintRegionId }: PoiDialogProps) => {
  const allPointOfInterestTemplates = useAtomValue(allPointOfInterestTemplatesState);
  const [selectedPOIType, setSelectedPOIType] = useState<string>('');
  //  const [searchTerms, setSearchTerms] = useState('');
  const [unfinishedSearchTerm, setUnfinishedSearchTerm] = useState('');
  const [pointOfInterests, setPointOfInterests] = useState<PointOfInterestsQueryType>([]);
  const [remediatePointOfInterest] = useUpdatePointOfInterestsStatusMutation();
  const [updatePaintRegionStatus] = useUpdatePaintRegionStatusMutation();
  const structureId = useGetStructureId();

  const [selectedPointOfInterestId, setSelectedPointOfInterestId] = useAtom(
    poiState.selectedPointOfInterestId
  );
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const allPOIsTemplatesWithAllTab: TemplateTypeWithAllTab[] = useMemo(
    () => [{ name: ALL_POIS_TAB_LABEL, type: '', fields: [] }, ...allPointOfInterestTemplates],
    [allPointOfInterestTemplates]
  );
  const [page, setPage] = useState(0);
  const [poiTotalCount, setPoiTotalCount] = useState(0);
  const selectedPoiTemplate = allPOIsTemplatesWithAllTab[0];
  const [selectedPois, setSelectedPois] = useState<SelectedPois>({});
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [sortBy, setSortBy] = useState('createdAt');
  // TODO
  // useEffect(() => {
  //   const delayDebounce = setTimeout(() => {
  //     setSearchTerms(unfinishedSearchTerm);
  //   }, 500);

  //   return () => clearTimeout(delayDebounce);
  // }, [setSearchTerms, unfinishedSearchTerm]);

  const handleSearchTermChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUnfinishedSearchTerm(event.target.value);
    },
    [setUnfinishedSearchTerm]
  );

  const type = upperSnakeCase(selectedPOIType);

  useEffect(() => {
    setSelectedPOIType(allPointOfInterestTemplates[0]?.name);
    setSelectedPois({});
  }, [allPointOfInterestTemplates]);

  const updatePoiState = useCallback((data: PointOfInterestsQuery) => {
    if (data?.pointOfInterests && data?.pointOfInterestsCount?.count !== undefined) {
      setPointOfInterests(data.pointOfInterests);
      setPoiTotalCount(data.pointOfInterestsCount.count ?? 0);
    }
  }, []);

  const { loading: poisLoading, refetch } = usePointOfInterestsQuery({
    variables: {
      input: {
        structureId,
        type,
        paintRegionIds: [paintRegionId],
      },
      pageInfo: {
        sortBy,
        sortDir: sortDirection,
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      },
    },
    skip: paintRegionId === '',
    fetchPolicy: 'cache-first',
    onCompleted(data) {
      updatePoiState(data);
    },
  });

  const handleMarkRemediate = useCallback(async () => {
    await remediatePointOfInterest({
      variables: {
        input: {
          pointOfInterestIds: Object.values(selectedPois).flat(),
          statusRemediatedThrough: StatusRemediatedThrough.PaintRegion,
          status: PoiStatus.Remediated,
        },
      },
      onCompleted: () => {
        refetch().then(({ data }) => {
          updatePoiState(data);
        });
      },
      refetchQueries: [PointOfInterestsDocument],
    });

    await updatePaintRegionStatus({
      variables: {
        input: {
          paintRegionId: paintRegionId,
          status: PaintRegionStatus.Remediated,
        },
      },
      refetchQueries: [AllPaintRegionsForPaintBlockTableDocument],
    });
    toggleDialog(false);
    setSelectedPois({});
  }, [
    paintRegionId,
    refetch,
    remediatePointOfInterest,
    selectedPois,
    toggleDialog,
    updatePaintRegionStatus,
    updatePoiState,
  ]);
  const poiColumns = usePoiColumns({
    selectedPoiTemplate,
    type,
  });
  const { rows } = usePoiData({
    pointOfInterests,
    columns: poiColumns,
    hiddenColumns: [],
  });

  const handlePoiSelected = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, poi: RowData) => {
      if ((event.target as HTMLInputElement).type === 'checkbox') {
        return;
      }
      setSelectedPointOfInterestId(
        poi.id.toString() === selectedPointOfInterestId ? undefined : poi.id.toString()
      );
    },
    [selectedPointOfInterestId, setSelectedPointOfInterestId]
  );

  const handlePointOfInterestSelect = useCallback(
    (selectedPointOfInterestIds: string[]) => {
      setSelectedPois((previousSelected) => {
        const newState = { ...previousSelected };

        if (!newState[selectedPOIType]) {
          newState[selectedPOIType] = [];
        }

        newState[selectedPOIType] = selectedPointOfInterestIds;
        return newState;
      });
    },
    [selectedPOIType]
  );

  const getTotalPoiCount = useMemo(() => {
    return Object.values(selectedPois).reduce((total, items) => total + (items?.length ?? 0), 0);
  }, [selectedPois]);

  const POITabs = useMemo(() => {
    return (
      <Can I="read" a="PointOfInterest">
        <Stack
          alignItems="center"
          direction="row"
          marginTop={2}
          maxWidth="80%"
          sx={{ ...scrollDesign, pb: 1 }}
        >
          {allPointOfInterestTemplates.map((template) => (
            <>
              {template && (
                <Stack key={template?.id} alignItems="center" direction="row" marginRight={1.5}>
                  <POIDialogTab
                    selected={selectedPOIType === template.name}
                    key={template.id}
                    onClick={() => {
                      setSelectedPOIType(template.name);
                    }}
                  >
                    <Typography variant="body2" width="max-content">
                      {template.name} {selectedPois[template.name]?.length ?? 0}
                    </Typography>
                  </POIDialogTab>
                </Stack>
              )}
            </>
          ))}
        </Stack>
      </Can>
    );
  }, [allPointOfInterestTemplates, selectedPOIType, selectedPois]);

  const handleSort = useCallback(
    (sortByField: string) => {
      const isAsc = sortBy === sortByField && sortDirection === 1;
      setSortDirection(isAsc ? -1 : 1);
      setSortBy(sortByField);
    },
    [setSortDirection, sortBy, sortDirection]
  );

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth sx={{ radius: 2 }}>
      <DialogTitle>
        <b>Select POIs to remediate</b>
      </DialogTitle>
      <Box display="flex" alignItems="center" justifyContent="space-between" paddingX={3}>
        {POITabs}
        <TextField
          data-testid="search-poi-field"
          id="standard-basic"
          label="Search POI"
          variant="standard"
          type="search"
          value={unfinishedSearchTerm}
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={handleSearchTermChanged}
        />
      </Box>

      <DialogContent>
        {poisLoading ? (
          <Box sx={{ minHeight: 400 }}>
            <PageLoader />
          </Box>
        ) : (
          <>
            {pointOfInterests ? (
              <Box sx={{ height: 400, width: '100%' }}>
                <CustomTable
                  columns={poiColumns}
                  data={rows ?? []}
                  totalCount={poiTotalCount}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={setPage}
                  onRowsPerPageChange={setRowsPerPage}
                  handleSort={handleSort}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onRowSelect={handlePointOfInterestSelect}
                  selectedRowIds={selectedPois[selectedPOIType] ?? []}
                  onRowClick={handlePoiSelected}
                  showGearIcon={false}
                  selectedRow={selectedPointOfInterestId}
                />
              </Box>
            ) : (
              <Typography variant="h5" sx={{ p: 2 }}>
                No Data Found
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: `1px solid ${abyssColors.primary[200]}`,
        }}
      >
        <Stack alignItems="center" direction="row" marginLeft={2}>
          <Typography sx={{ color: abyssColors.primary[400] }}>Total POI select:</Typography>
          <Typography fontWeight="700">{getTotalPoiCount}</Typography>
        </Stack>
        <Box>
          <Button
            id="cancel-mark-poi-remediate"
            onClick={() => {
              toggleDialog(false);
              setSelectedPois({});
            }}
            color="primary"
            sx={{ marginRight: 3 }}
          >
            Cancel
          </Button>
          <Button
            id="mark-poi-remediate"
            onClick={handleMarkRemediate}
            variant="contained"
            sx={{
              maxHeight: '34px',
              backgroundColor: abyssColors.primary[500],
              borderRadius: '4px',
              padding: '8px 12px',
              marginRight: 3,
            }}
          >
            Mark as remediated
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
